import { Close } from '@volkswagen-onehub/icons-core';
import * as React from 'react';
import { Transition, TransitionGroup } from 'react-transition-group';
import { keyframes, withTheme } from 'styled-components';
import { Text, TokenTextAppearance, TokenTextColor } from '../text';
import { css, styled } from '../theme-provider';
import { getCommonProps } from '../helpers/common-props';
const keyframesSubActionsShow = (percents, pixels) => keyframes `0%,50%{transform:translateY(${percents}%)translateY(+${pixels}px);}100%{transform:translateY(0);}`;
const keyframesSubActionsHide = (percents, pixels) => keyframes `0%{transform:translateY(0);}50%,100%{transform:translateY(${percents}%)translateY(+${pixels}px);}`;
const keyframesCollapse = (props) => {
    return keyframes `from{transform:translateX(0);}to{transform:${props.theme.direction === 'rtl'
        ? `translateX(
							${parseInt(props.theme.size.static350, 10) +
            parseInt(props.theme.size.static300, 10) * 2}px
						)
						translateX(-100%)`
        : `translateX(
							-${parseInt(props.theme.size.static350, 10) +
            parseInt(props.theme.size.static300, 10) * 2}px
						)
						translateX(100%)`};}`;
};
const keyframesExpand = (props) => {
    return keyframes `from{transform:${props.theme.direction === 'rtl'
        ? `translateX(
							${parseInt(props.theme.size.static350, 10) +
            parseInt(props.theme.size.static350, 10) * 2}px
						)
						translateX(-100%)`
        : `translateX(
							-${parseInt(props.theme.size.static350, 10) +
            parseInt(props.theme.size.static350, 10) * 2}px
						)
						translateX(100%)`};}to{transform:translateX(0);}`;
};
const keyframesInitializing = keyframes `0%{opacity:0;}100%{opacity:1;}`;
const StyledOverflowWrapper = styled.div.withConfig({ displayName: "StyledOverflowWrapper", componentId: "sc-11iuvw0" }) `direction:${(props) => props.theme.direction || 'ltr'};margin-inline-start:auto;overflow:hidden;clip-path:inset(0 0 0 0);border-radius:${(props) => parseInt(props.theme.size.static350, 10) / 2 +
    parseInt(props.theme.size.static300, 10)}px;width:100%;`;
const height = (props) => `${
/* icon size plus top and bottom margins */
parseInt(props.theme.size.static350, 10) +
    parseInt(props.theme.size.static300, 10) * 2}px`;
const StyledTextWrapper = styled.div.withConfig({ displayName: "StyledTextWrapper", componentId: "sc-1s5l1d7" }) `${(props) => props.expanded
    ? css `
					animation: ${props.theme.animation.duration.nba} ${keyframesExpand} 1
						${props.theme.animation.timingFunction.cubicOut};
				`
    : css `
					animation: ${props.theme.animation.duration.nba} ${keyframesCollapse}
						1 ${props.theme.animation.timingFunction.cubicOut};
				`} transform:translateX(0);white-space:nowrap;transition:background-color ${(props) => `${props.theme.animation.duration.nba} ${props.theme.animation.timingFunction.cubicOut}`},border-color ${(props) => `${props.theme.animation.duration.nba} ${props.theme.animation.timingFunction.cubicOut}`};height:${height};${(props) => props.theme.direction === 'rtl'
    ? css `
					padding-left: ${parseInt(props.theme.size.static350, 10) +
        parseInt(props.theme.size.static200, 10) +
        parseInt(props.theme.size.static300, 10)}px; /* icon size plus large outer and small inner border */
					padding-right: ${props.theme.size.static300};
				`
    : css `
					padding-right: ${parseInt(props.theme.size.static350, 10) +
        parseInt(props.theme.size.static200, 10) +
        parseInt(props.theme.size.static300, 10)}px; /* icon size plus large outer and small inner border */
					padding-left: ${props.theme.size.static300};
				`} border-radius:${(props) => parseInt(props.theme.size.static350, 10) / 2 +
    parseInt(props.theme.size.static300, 10)}px;background-color:${({ theme }) => theme.colors.accent.nbab.default};flex:auto 0 0;display:flex;align-items:center;`;
const StyledIconWrapper = styled.div.withConfig({ displayName: "StyledIconWrapper", componentId: "sc-40ei28" }) `align-items:center;display:flex;flex:auto 0 0;height:${height};justify-content:center;position:absolute;border:${({ theme }) => theme.size.static300} solid;border-color:${({ theme }) => theme.direction === 'rtl'
    ? `transparent transparent transparent ${theme.colors.accent.nbab.default}`
    : `transparent ${theme.colors.accent.nbab.default} transparent transparent`};border-radius:${({ theme }) => parseInt(theme.size.static350, 10) / 2 + parseInt(theme.size.static300, 10)}px;left:${({ theme }) => (theme.direction === 'rtl' ? 0 : 'unset')};right:${({ theme }) => (theme.direction === 'rtl' ? 'unset' : 0)};transition:border-color ${({ theme }) => theme.animation.duration.nba} ${({ theme }) => theme.animation.timingFunction.cubicOut};width:${({ theme }) => parseInt(theme.size.static350, 10) + parseInt(theme.size.static300, 10) * 2}px;`;
const StyledDecorativeIconWrapper = styled.div.withConfig({ displayName: "StyledDecorativeIconWrapper", componentId: "sc-5vg8x4" }) `background-color:{({theme})=> theme.colors.accent.nbab.default};height:100%;width:100%;`;
const StyledInnerIconWrapper = styled.div.withConfig({ displayName: "StyledInnerIconWrapper", componentId: "sc-9c8a1e" }) `height:${(props) => parseInt(props.theme.size.static350, 10) +
    parseInt(props.theme.size.static200, 10) * 2}px;width:${(props) => parseInt(props.theme.size.static350, 10) +
    parseInt(props.theme.size.static200, 10) * 2}px;background-color:${({ theme, transitionState }) => transitionState === 'entering'
    ? theme.colors.content.inverted
    : theme.colors.accent.nbab.default};border:${(props) => props.theme.size.static200} solid;border-radius:${(props) => parseInt(props.theme.size.static350, 10) / 2 +
    parseInt(props.theme.size.static200, 10)}px;border-color:${({ theme, transitionState }) => transitionState === 'entering'
    ? theme.colors.content.inverted
    : theme.colors.accent.nbab.default};line-height:0;position:absolute;${(props) => props.theme.direction === 'rtl'
    ? `
					right: -${props.theme.size.static200};
			  `
    : `
					left: -${props.theme.size.static200};
			  `} top:-${(props) => props.theme.size.static200};transition:opacity ${(props) => `${props.theme.animation.duration.nba} ${props.theme.animation.timingFunction.cubicOut}`},transform ${(props) => `${props.theme.animation.duration.nba} ${props.theme.animation.timingFunction.cubicOut}`},background-color ${(props) => `${props.theme.animation.duration.nba} ${props.theme.animation.timingFunction.cubicOut}`},border-color ${(props) => `${props.theme.animation.duration.nba} ${props.theme.animation.timingFunction.cubicOut}`};opacity:${(props) => (props.transitionState === 'entered' ? 1 : 0)};transform:${(props) => {
    if (props.transitionState === 'entering') {
        return 'rotate(-90deg)';
    }
    if (props.transitionState === 'entered') {
        return 'rotate(0deg)';
    }
    return 'rotate(90deg)';
}};`;
const StyledSubActionWrapper = styled.div.withConfig({ displayName: "StyledSubActionWrapper", componentId: "sc-n3pvt1" }) `position:absolute;bottom:${height};`;
const StyledButton = styled.button.withConfig({ displayName: "StyledButton", componentId: "sc-1k80dwj" }) `appearance:none;background-color:transparent;border:none;box-shadow:none;cursor:pointer;pointer-events:auto;display:flex;min-width:${height};padding:0px;position:relative;text-decoration:none;visibility:${({ show }) => (show ? 'visible' : 'hidden')};${({ theme }) => css `
		/* We hide the element until the initial animations are done */
		animation: ${theme.animation.duration.nba} ${keyframesInitializing} 1
			step-end;
		color: ${theme.colors.button.primary.default};
		border-radius: ${parseInt(theme.size.static350, 10) / 2 +
    parseInt(theme.size.static300, 10)}px; /* 1/2 height */
		${theme.direction === 'rtl' ? 'margin-right: auto;' : 'margin-left: auto;'}
	`} ${({ fullWidth }) => fullWidth && 'width: 100%;'} ${({ expanded, theme }) => expanded
    ? css `
					max-width: calc(100vw - (2 * ${theme.size.dynamic0200}));
					transition: none;
				`
    : css `
					max-width: ${height};
					transition: max-width 0s ease-in-out ${theme.animation.duration.nba};
				`}::before{content:"";position:absolute;${({ theme }) => (theme.direction === 'rtl' ? 'left: 0;' : 'right: 0;')} z-index:1;width:${height};height:100%;border-radius:${({ theme }) => parseInt(theme.size.static350, 10) / 2 +
    parseInt(theme.size.static300, 10)}px;transition:${({ expanded, theme }) => expanded
    ? 'none'
    : `visibility ${theme.animation.duration.nba}, width ${theme.animation.duration.nba} ${theme.animation.timingFunction.cubicOut}`};pointer-events:none;}:hover{::before{width:100%;background:${({ theme }) => `${theme.colors.hoverIndicator}33`};}}:hover,:focus{${({ theme, expanded }) => css `
			transition:
				visibility ${theme.animation.duration.nba},
				max-width ${theme.animation.duration.nba}
					${theme.animation.timingFunction.cubicOut};
			${StyledTextWrapper} {
				${expanded === undefined &&
    css `
					animation: ${theme.animation.duration.nba} ${keyframesExpand} 1
						${theme.animation.timingFunction.cubicOut};
				`}
			}
		`};${({ show, expanded, theme }) => show &&
    expanded === undefined &&
    css `
				max-width: calc(100vw - (2 * ${theme.size.dynamic0200}));
			`};}:active{::before{background:${({ theme }) => `${theme.colors.hoverIndicator}66`};}}:focus{color:${({ theme }) => theme.colors.button.primary.default};outline:none;box-shadow:0 0 0 2px ${({ theme }) => theme.colors.button.primary.default};${StyledTextWrapper}{background-color:${({ theme }) => theme.colors.accent.nbab.default};}${StyledIconWrapper}{${({ theme }) => theme.direction === 'rtl'
    ? `border-left-color: ${theme.colors.accent.nbab.default};
					border-right-color: transparent;`
    : `border-left-color: transparent;
					border-right-color: ${theme.colors.accent.nbab.default};`}}${StyledInnerIconWrapper}{background-color:${({ theme }) => theme.colors.accent.nbab.default};border-color:${({ theme }) => theme.colors.accent.nbab.default};}}`;
const StyledCloseButton = styled.button.withConfig({ displayName: "StyledCloseButton", componentId: "sc-tnsddf" }) `${({ theme }) => (theme.direction === 'rtl' ? 'left: 0;' : 'right: 0;')} background-color:${({ theme }) => theme.colors.button.primary.disabled};border-radius:${({ theme }) => parseInt(theme.size.static350, 10) / 2 + parseInt(theme.size.static300, 10)}px;color:${({ theme }) => theme.colors.content.disabled};&::before{content:"";position:absolute;${({ theme }) => (theme.direction === 'rtl' ? 'left: 0;' : 'right: 0;')} z-index:1;width:${height};height:100%;border-radius:${({ theme }) => parseInt(theme.size.static350, 10) / 2 +
    parseInt(theme.size.static300, 10)}px;pointer-events:none;}&:hover{&::before{width:100%;background:${({ theme }) => `${theme.colors.hoverIndicator}33`};}}&:active{&::before{background:${({ theme }) => `${theme.colors.hoverIndicator}66`};}}&:focus{outline:none;box-shadow:0 0 0 2px ${({ theme }) => theme.colors.button.primary.default};}visibility:${({ show }) => (show ? 'visible' : 'hidden')};opacity:${({ show }) => (show ? '1' : '0')};appearance:none;border:none;box-shadow:none;cursor:pointer;pointer-events:auto;display:flex;min-height:${height};min-width:${height};padding:0px;position:absolute;text-decoration:none;align-items:center;justify-content:center;`;
const StyledSubNextBestActionButton = styled.div.withConfig({ displayName: "StyledSubNextBestActionButton", componentId: "sc-1ptd3oc" }) `width:100%;${({ theme }) => `
		margin: ${theme.size.static100} 0;
		transition: opacity ${theme.animation.duration.nba} ${theme.animation.timingFunction.cubicOut};

		&:last-child {
			margin-bottom: ${theme.size.static150};
		}
	`} ${({ show, theme }) => `
		visibility: ${show ? 'visible' : 'hidden'};
		opacity: ${show ? '1' : '0'};
		transition-delay: ${show ? theme.animation.duration.nba : 0};

		& ${StyledButton}{
			pointer-events: ${show ? 'auto' : 'none'};
		}
	`} ${({ show, endIndex, theme }) => show
    ? css `
					animation: ${keyframesSubActionsShow(endIndex * 100, Number(theme.size.static100.replace('px', '') || 0) * endIndex * 2)}
						${theme.animation.duration.collapsible}
						${theme.animation.timingFunction.cubicOut};
				`
    : css `
					animation: ${keyframesSubActionsHide(endIndex * 100, Number(theme.size.static100.replace('px', '') || 0) * endIndex * 2)}
						${theme.animation.duration.collapsible}
						${theme.animation.timingFunction.cubicOut};
				`}`;
const StyledNextBestActionButtonWrapper = styled.div.withConfig({ displayName: "StyledNextBestActionButtonWrapper", componentId: "sc-zzq582" }) `isolation:isolate;align-items:flex-end;display:flex;flex-direction:column;justify-content:flex-end;${({ theme }) => theme.direction === 'rtl' ? 'margin-right: auto;' : 'margin-left: auto;'};width:${({ fullWidth }) => (fullWidth ? '100%' : 'max-content')};pointer-events:none;`;
const isLink = (action) => Boolean(action.tag === 'a');
const StyledBadge = styled.div.withConfig({ displayName: "StyledBadge", componentId: "sc-s9i9tg" }) `${({ theme }) => css `
		background-color: ${theme.colors.background.primary};
		border-color: ${theme.colors.button.primary.default};
		border: ${theme.size.border.heavy} solid;
		min-height: ${theme.size.static300};
		min-width: ${theme.size.static300};
		${theme.direction === 'rtl' ? 'left: 0' : 'right: 0'};
	`} border-radius:50%;display:flex;justify-content:center;position:absolute;z-index:1;`;
const StyledBadgeText = styled.div.withConfig({ displayName: "StyledBadgeText", componentId: "sc-1fcr1s2" }) `${({ theme }) => css `
		color: ${theme.colors.button.primary.default};
		font-size: ${theme.size.static200};
		line-height: ${theme.size.static200};
	`} background:transparent;display:flex;flex-direction:column;justify-content:center;`;
const NextBestActionButtonInternal = React.forwardRef((props, forwardRef) => {
    const { action, closeButtonAriaLabel, disabled, displaySubActions, expandContent, fullWidth, hideIcon, subActions, tabIndex = undefined, theme, } = props;
    const [internalDisplaySubActions, setInternalDisplaySubActions] = React.useState(false);
    const CloseButtonRef = React.useRef(null);
    const timeout = parseInt(theme.animation.duration.nba || '300', 10);
    const commonProps = getCommonProps(props);
    React.useEffect(() => {
        if (typeof displaySubActions === 'boolean' &&
            displaySubActions !== internalDisplaySubActions) {
            setInternalDisplaySubActions(displaySubActions);
        }
    }, [displaySubActions, internalDisplaySubActions]);
    React.useEffect(() => {
        if (internalDisplaySubActions) {
            // there is a focus delay due to animation
            setTimeout(() => {
                var _a;
                (_a = CloseButtonRef.current) === null || _a === void 0 ? void 0 : _a.focus();
            }, timeout);
        }
    }, [internalDisplaySubActions, timeout]);
    // biome-ignore lint/suspicious/noExplicitAny: there is no sane type for the ref of both StyledLink and StyledButton using "as" that works with both components and both tags, so we have to use "any" instead
    const ref = forwardRef;
    const NBAContent = (React.createElement(React.Fragment, null,
        React.createElement(StyledOverflowWrapper, null,
            React.createElement(StyledTextWrapper, { expanded: expandContent },
                React.createElement(Text, { appearance: TokenTextAppearance.label200, staticSize: true, color: TokenTextColor.inherit }, action.content))),
        React.createElement(StyledIconWrapper, null,
            React.createElement(StyledDecorativeIconWrapper, null,
                React.createElement(TransitionGroup, null, hideIcon ? null : (React.createElement(Transition, { timeout: { exit: timeout }, key: action.icon.key || undefined }, (state) => (React.createElement(StyledInnerIconWrapper, { transitionState: state }, action.icon))))))),
        (subActions === null || subActions === void 0 ? void 0 : subActions.length) ? (React.createElement(StyledBadge, null,
            React.createElement(StyledBadgeText, null,
                React.createElement("bdi", null, `+${subActions === null || subActions === void 0 ? void 0 : subActions.length}`)))) : null));
    const toggleDisplaySubActions = () => {
        setInternalDisplaySubActions(!internalDisplaySubActions);
    };
    const handleOnClick = (evt) => {
        if (subActions === null || subActions === void 0 ? void 0 : subActions.length) {
            toggleDisplaySubActions();
        }
        action.onClick && action.onClick(evt);
    };
    // since the main NBAB is either a link or a button it gets different props for each
    const mainNBABDifferingProps = isLink(action)
        ? {
            as: 'a',
            onClick: action.onClick,
            href: action.href,
            show: true,
        }
        : {
            onClick: handleOnClick,
            show: !internalDisplaySubActions,
        };
    return (React.createElement(StyledNextBestActionButtonWrapper, Object.assign({ fullWidth: fullWidth }, commonProps),
        (subActions === null || subActions === void 0 ? void 0 : subActions.length) ? (React.createElement(StyledSubActionWrapper, null, subActions.map((actionItem, actionIndex) => {
            const endIndex = subActions.length - actionIndex;
            return (React.createElement(StyledSubNextBestActionButton, { key: actionItem.key, show: internalDisplaySubActions, endIndex: endIndex, "aria-hidden": !internalDisplaySubActions }, actionItem));
        }))) : null,
        React.createElement(StyledButton, Object.assign({}, mainNBABDifferingProps, { ref: ref, expanded: expandContent, fullWidth: fullWidth, "aria-label": action.ariaLabel, tabIndex: tabIndex, disabled: disabled }), NBAContent),
        (subActions === null || subActions === void 0 ? void 0 : subActions.length) ? (React.createElement(StyledCloseButton, { "aria-label": closeButtonAriaLabel, onClick: handleOnClick, show: internalDisplaySubActions, ref: CloseButtonRef },
            React.createElement(Close, { ariaHidden: true }))) : null));
});
NextBestActionButtonInternal.displayName = 'NextBestActionButtonInternal';
export const NextBestActionButton = withTheme(NextBestActionButtonInternal);
